import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Search, Bell, Menu } from "lucide-react";
import { navItems } from "./sidebarNavItems";
import { LanguageDropdown } from "@/shared/components/common/LanguageDropDown";
import { InstanceStatusIndicator } from "@/shared/ec2Instance";

interface DashboardHeaderProps {
  onMenuClick: () => void;
  currentPage: string;
}

export function DashboardHeader({
  onMenuClick,
  currentPage,
}: DashboardHeaderProps) {
  const { t } = useTranslation("common");
  const location = useLocation();
  const isContentStudioSubPage =
    location.pathname.startsWith("/content-studio/") &&
    location.pathname !== "/content-studio";
  const isContentStudio = location.pathname.startsWith("/content-studio");

  const currentNav = navItems.find((item) => item.key === currentPage);
  const heading = currentNav
    ? t(currentNav.label)
    : t("sidebar.dashboard", "Dashboard");

  return (
    <header className="bg-white px-[1.88rem] py-[0.75rem] rounded-tl-3xl border-b border-[#E5E7EB] shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-[0.75rem]">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={onMenuClick}
          >
            <Menu className="w-5 h-5" />
          </Button>
          <p className="text-title-primary">
            {heading}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* EC2 Instance Running Status Indicator */}
          {isContentStudio && <InstanceStatusIndicator />}

          {/* Search bar - hidden on content studio subpages */}
          {!isContentStudioSubPage && (
            <div className="flex items-center gap-2 min-w-[16rem] md:w-[23.75rem]">
              <div className="relative flex-1 max-w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" />
                <Input
                  placeholder={t("actions.search")}
                  className="pl-10 bg-[var(--surface-subtle-hex)] border-gray-200 search-text-regular text-muted-secondary"
                />
              </div>
            </div>
          )}

          <LanguageDropdown />
          <Button variant="ghost" size="icon" className="relative">
            <Bell className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </header>
  );
}

